import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PlatformIcon, PLATFORM_META } from "@/components/PlatformBadge";
import { relativeTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Check, Loader2, RefreshCw, AlertTriangle, X, Settings2, ShieldCheck,
  ArrowRight, CircleDot, HelpCircle, Lock, Copy, ExternalLink,
} from "lucide-react";

const SOCIAL = ["facebook", "instagram", "linkedin"];
const FREQ = [1, 3, 6, 12, 24];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PROVIDER = { facebook: "meta", instagram: "meta", linkedin: "linkedin", ga4: "google" };
const PROVIDER_PLATFORMS = { meta: ["facebook", "instagram"], linkedin: ["linkedin"], google: ["ga4"] };

const PREREQS = {
  facebook: [
    "You have access to the Facebook Page you want to connect.",
    "You have sufficient permissions to manage the Page.",
    "You are logged into the correct Facebook account.",
  ],
  instagram: [
    "Your Instagram account is a Business or Creator account.",
    "It is connected to the associated Facebook Page.",
    "You have the appropriate permissions for that Facebook Page.",
  ],
  linkedin: [
    "You are logged into the correct LinkedIn account.",
    "You have administrator access to the LinkedIn Page you want to connect.",
  ],
  ga4: [
    "You have access to the Google Analytics property for this website.",
    "You are using the Google account that has access to the GA4 property.",
  ],
};
const HOWTO = {
  facebook: ["One-time: add your Meta app keys under \u201CManage app credentials\u201D (full guide is there).", "Log into the correct Facebook account.", "Make sure you can manage the Page.", "Click Connect Facebook and authorize Nuera.", "Select your Page. Done."],
  instagram: ["One-time: add your Meta app keys under \u201CManage app credentials\u201D.", "Ensure your Instagram is a Business account linked to the correct Facebook Page.", "Click Connect Instagram and authorize via Meta.", "Select the Instagram account. Done."],
  linkedin: ["One-time: add your LinkedIn app keys under \u201CManage app credentials\u201D.", "Log into the correct LinkedIn account with admin access to the Page.", "Click Connect LinkedIn and authorize Nuera.", "Select the LinkedIn Page. Done."],
  ga4: ["One-time: add your Google app keys under \u201CManage app credentials\u201D.", "Use the Google account with access to the GA4 property.", "Click Connect Google Analytics and authorize Nuera.", "Select the GA4 property. Done."],
};
const SELECT_LABEL = { facebook: "Select your Facebook Page", instagram: "Select Instagram Account", linkedin: "Select LinkedIn Page", ga4: "Select Website / GA4 Property" };
const ERROR_HINT = {
  facebook: "You may not have sufficient access to the Page. Make sure you're logged into the correct Facebook account and have the required Page permissions.",
  instagram: "Your Instagram account needs to be a Business account connected to a Facebook Page before Nuera can access it.",
  linkedin: "Your LinkedIn account does not appear to have administrator access to this Page.",
  ga4: "We couldn't find a GA4 property for this Google account. Please check you have access to the correct property.",
};
const CONNECT_SUB = { facebook: "Connect your Facebook Page", instagram: "Connect your Instagram Business account", linkedin: "Connect your LinkedIn Page", ga4: "Connect your GA4 property" };

const DOMAIN = (BACKEND || "").replace(/^https?:\/\//, "");

const GUIDES = {
  meta: {
    portal: "https://developers.facebook.com/apps",
    steps: [
      "Open the Meta portal and click Create app \u2192 choose the Business type.",
      "Add products: \u201CFacebook Login for Business\u201D and \u201CInstagram\u201D (Instagram API with Facebook Login).",
      `In App settings \u2192 Basic: add \u201C${DOMAIN}\u201D to \u201CApp Domains\u201D, then \u201C+ Add Platform\u201D \u2192 Website and set Site URL to ${BACKEND}. (App ID = Client ID, App secret = Client Secret.)`,
      "Under Facebook Login \u2192 Settings, paste the redirect URI (below) into \u201CValid OAuth Redirect URIs\u201D.",
      "Copy the App ID & App secret into the fields on the left and click Save.",
    ],
  },
  linkedin: {
    portal: "https://www.linkedin.com/developers/apps",
    steps: [
      "Open the LinkedIn developer portal and click Create app (link it to your Company Page).",
      "On the Products tab, request \u201CCommunity Management API\u201D.",
      "On the Auth tab you'll find your Client ID and Client Secret.",
      "On the Auth tab, add the redirect URI (below) under Authorized redirect URLs.",
      "Copy the Client ID & Secret into the fields on the left and click Save.",
    ],
  },
  google: {
    portal: "https://console.cloud.google.com/apis/credentials",
    steps: [
      "Open Google Cloud Console and create or select a project.",
      "In APIs & Services \u2192 Library, enable \u201CGoogle Analytics Data API\u201D and \u201CAnalytics Admin API\u201D.",
      "Configure the OAuth consent screen (External) and add your email as a test user.",
      "In Credentials, create an OAuth client ID of type \u201CWeb application\u201D.",
      "Add the redirect URI (below) to Authorized redirect URIs and your site origin to Authorized JavaScript origins.",
      "Copy the Client ID & Client secret into the fields on the left and click Save.",
    ],
  },
};

function StatusPill({ status }) {
  const map = {
    connected: { t: "Connected", c: "bg-emerald-50 text-emerald-700 border-emerald-200", I: Check },
    needs_reconnect: { t: "Action required", c: "bg-amber-50 text-amber-700 border-amber-200", I: AlertTriangle },
    error: { t: "Connection failed", c: "bg-rose-50 text-rose-700 border-rose-200", I: X },
    syncing: { t: "Syncing…", c: "bg-blue-50 text-blue-700 border-blue-200", I: RefreshCw },
    not_connected: { t: "Not connected", c: "bg-muted text-muted-foreground border-border", I: CircleDot },
    disconnected: { t: "Not connected", c: "bg-muted text-muted-foreground border-border", I: CircleDot },
  };
  const s = map[status] || map.not_connected;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${s.c}`}>
      <s.I className={`h-3.5 w-3.5 ${status === "syncing" ? "animate-spin" : ""}`} /> {s.t}
    </span>
  );
}

export default function Connections() {
  const { org, user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";
  const canSync = isAdmin || user?.role === "manager";

  const { data: integrations = [] } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/integrations")).data });
  const { data: settings } = useQuery({ queryKey: ["sync-settings"], queryFn: async () => (await api.get("/settings/sync")).data });

  const [flow, setFlow] = useState(null); // {platform, step, options, selected}
  const [manage, setManage] = useState(null);
  const [howto, setHowto] = useState(null);
  const [busy, setBusy] = useState(false);

  const connectedCount = integrations.filter((i) => i.connected).length;
  const lastUpdated = integrations.reduce((acc, i) => (i.last_successful_sync && (!acc || i.last_successful_sync > acc) ? i.last_successful_sync : acc), null);

  const { data: creds } = useQuery({ queryKey: ["app-creds"], queryFn: async () => (await api.get("/integrations/app-credentials")).data });
  const [credForm, setCredForm] = useState({});
  const [showCreds, setShowCreds] = useState(false);
  const [savingProv, setSavingProv] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const err = q.get("connect_error");
    const connect = q.get("connect");
    if (err) {
      toast.error(err === "no_credentials" ? "Add your app credentials first" : "We couldn't complete that connection. Please try again.");
      window.history.replaceState({}, "", "/settings");
    } else if (connect && q.get("status") === "select") {
      (async () => {
        try {
          const { data } = await api.get(`/oauth/${connect}/options`);
          setFlow({ platform: connect, step: "select", options: data.options, selected: data.options[0]?.id });
        } catch { toast.error("Please start the connection again."); }
        window.history.replaceState({}, "", "/settings");
      })();
    }
  }, []);

  const saveCreds = async (platform) => {
    const provider = PROVIDER[platform];
    const f = credForm[provider] || {};
    if (!f.client_id || !f.client_secret) { toast.error("Enter both Client ID and Secret"); return; }
    setSavingProv(provider);
    try {
      await api.put("/integrations/app-credentials", { platform, client_id: f.client_id, client_secret: f.client_secret });
      await qc.invalidateQueries({ queryKey: ["app-creds"] });
      toast.success("Credentials saved — now click Connect below");
      setCredForm((c) => ({ ...c, [provider]: {} }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save credentials"); }
    finally { setSavingProv(null); }
  };

  const startConnect = (platform) => {
    const provider = PROVIDER[platform];
    if (!creds?.[provider]?.configured) {
      toast.error(`Add your ${PLATFORM_META[platform].label} app credentials first`);
      setShowCreds(true);
      return;
    }
    setFlow({ platform, step: "prereq", options: [], selected: null });
  };

  const doAuthorize = () => {
    const token = localStorage.getItem("nuera_token");
    window.location.href = `${BACKEND}/api/oauth/${PROVIDER[flow.platform]}/start?t=${encodeURIComponent(token)}&platform=${flow.platform}`;
  };

  const doConnect = async () => {
    const opt = flow.options.find((o) => o.id === flow.selected);
    setFlow((f) => ({ ...f, step: "connecting" }));
    try {
      const { data } = await api.post("/oauth/finalize", { platform: flow.platform, account_id: opt?.id });
      qc.invalidateQueries();
      setFlow((f) => ({ ...f, step: "done", account: { name: data.account_name } }));
    } catch {
      setFlow((f) => ({ ...f, step: "error" }));
    }
  };

  const doSync = async (platform) => {
    setBusy(true);
    try {
      const { data } = await api.post("/sync/now", { platform });
      toast.success(`${PLATFORM_META[platform].label} synced — ${data.records.toLocaleString()} records updated`);
      qc.invalidateQueries();
    } catch { toast.error("Sync failed. Please try again."); }
    finally { setBusy(false); }
  };

  const doReconnect = async (platform) => {
    setBusy(true);
    try { await api.post("/integrations/reconnect", { platform }); qc.invalidateQueries(); toast.success("Reconnected"); setManage(null); }
    catch { toast.error("Reconnect failed"); } finally { setBusy(false); }
  };

  const doDisconnect = async (platform) => {
    setBusy(true);
    try { await api.post("/integrations/disconnect", { platform }); qc.invalidateQueries(); toast.success("Disconnected"); setManage(null); }
    catch { toast.error("Disconnect failed"); } finally { setBusy(false); }
  };

  const setFreq = async (v) => {
    try { await api.put("/settings/sync", { frequency_hours: Number(v) }); qc.invalidateQueries({ queryKey: ["sync-settings"] }); toast.success(`Syncing every ${v} hours`); }
    catch { toast.error("Could not update frequency"); }
  };

  const meta = flow ? PLATFORM_META[flow.platform] : null;

  const setupSteps = [
    { label: "Brand Profile", done: !!org?.website },
    { label: "Connect Social Accounts", done: SOCIAL.every((p) => integrations.find((i) => i.platform === p)?.connected), partial: SOCIAL.filter((p) => integrations.find((i) => i.platform === p)?.connected).length },
    { label: "Connect Website Analytics", done: integrations.find((i) => i.platform === "ga4")?.connected },
    { label: "Start Data Sync", done: !!lastUpdated },
    { label: "View Dashboard", done: !!org?.onboarded },
  ];

  return (
    <div className="space-y-8" data-testid="connections">
      {/* checklist header */}
      <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-sm sm:p-8">
        <h2 className="font-heading text-2xl font-bold tracking-tight">Connect Your Marketing Channels</h2>
        <p className="mt-1 text-muted-foreground">Bring your social media and website analytics into Nuera. We handle the technical bits.</p>
        <Button variant="outline" size="sm" className="mt-3 rounded-full" data-testid="toggle-credentials" onClick={() => setShowCreds((v) => !v)}>{showCreds ? "Hide" : "Manage"} app credentials</Button>
        <div className="mt-5 flex flex-col gap-3 rounded-2xl bg-muted/40 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="font-semibold" data-testid="connections-count">{connectedCount} of 4 channels connected</span>
            {integrations.map((i) => (
              <span key={i.platform} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                {i.connected ? <Check className="h-4 w-4 text-emerald-600" /> : <CircleDot className="h-4 w-4" />} {i.label}
              </span>
            ))}
          </div>
          <Progress value={(connectedCount / 4) * 100} className="h-2 w-full sm:w-40" />
        </div>
      </div>

      {/* setup progress */}
      <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-sm">
        <p className="mb-4 text-sm font-semibold">Set up your Nuera dashboard</p>
        <div className="grid gap-3 sm:grid-cols-5">
          {setupSteps.map((s, idx) => (
            <div key={s.label} className="rounded-2xl border border-border/60 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-bold text-muted-foreground">{idx + 1}</span>
                {s.done ? <Check className="h-4 w-4 text-emerald-600" /> : s.partial ? <span className="text-[11px] font-semibold text-amber-600">{Math.round((s.partial / 3) * 100)}%</span> : <CircleDot className="h-4 w-4 text-muted-foreground" />}
              </div>
              <p className="text-xs font-medium leading-tight">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* app credentials */}
      {showCreds && (
        <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-sm" data-testid="app-credentials">
          <p className="font-semibold">App credentials</p>
          <p className="mb-4 text-sm text-muted-foreground">Paste your developer-app keys. Stored encrypted — the secret is never shown again.</p>
          <div className="grid gap-4">
            {[["facebook", "Meta (Facebook & Instagram)"], ["linkedin", "LinkedIn"], ["ga4", "Google (GA4)"]].map(([p, label]) => {
              const provider = PROVIDER[p];
              const cfg = creds?.[provider];
              const uri = `${BACKEND}/api/oauth/${provider}/callback`;
              return (
                <div key={p} className="rounded-2xl border border-border/60 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-semibold">{label}</p>
                    {cfg?.configured && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><Check className="h-3.5 w-3.5" />Configured</span>}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Input placeholder="Client ID / App ID" data-testid={`cred-id-${provider}`} value={(credForm[provider] || {}).client_id || ""} onChange={(e) => setCredForm((c) => ({ ...c, [provider]: { ...(c[provider] || {}), client_id: e.target.value } }))} />
                      <Input placeholder="Client Secret / App Secret" type="password" data-testid={`cred-secret-${provider}`} value={(credForm[provider] || {}).client_secret || ""} onChange={(e) => setCredForm((c) => ({ ...c, [provider]: { ...(c[provider] || {}), client_secret: e.target.value } }))} />
                      <Button size="sm" className="w-full rounded-full bg-brand-primary text-brand-primary-foreground" data-testid={`cred-save-${provider}`} disabled={savingProv === provider} onClick={() => saveCreds(p)}>{savingProv === provider ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save credentials</Button>
                      {cfg?.configured && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {PROVIDER_PLATFORMS[provider].map((pl) => (
                            <Button key={pl} size="sm" className="nuera-gradient flex-1 rounded-full text-white" data-testid={`cred-connect-${pl}`} onClick={() => startConnect(pl)}>
                              Connect {PLATFORM_META[pl].label} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                            </Button>
                          ))}
                        </div>
                      )}
                      <div>
                        <p className="mb-1 text-[11px] font-medium text-muted-foreground">Redirect URI to register in your app:</p>
                        <button type="button" data-testid={`copy-redirect-${provider}`} onClick={() => { navigator.clipboard.writeText(uri); toast.success("Redirect URI copied"); }} className="flex w-full items-center justify-between gap-2 rounded-lg border border-dashed border-border px-2 py-1.5 text-left text-[11px] text-muted-foreground hover:bg-muted/50">
                          <span className="truncate">{uri}</span>
                          <Copy className="h-3.5 w-3.5 shrink-0" />
                        </button>
                      </div>
                    </div>
                    <div className="rounded-xl bg-muted/40 p-3">
                      <div className="mb-1.5 flex items-center justify-between">
                        <p className="text-xs font-semibold">How to get these keys</p>
                        <a href={GUIDES[provider].portal} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-primary hover:underline">Open portal <ExternalLink className="h-3 w-3" /></a>
                      </div>
                      <ol className="space-y-1.5">
                        {GUIDES[provider].steps.map((s, i) => (
                          <li key={i} className="flex gap-2 text-[11px] leading-snug text-muted-foreground"><span className="font-bold text-foreground">{i + 1}.</span><span>{s}</span></li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* cards */}
      <div className="grid gap-5 md:grid-cols-2">
        {integrations.map((it) => {
          const m = PLATFORM_META[it.platform];
          return (
            <div key={it.platform} data-testid={`conn-card-${it.platform}`} className="flex flex-col rounded-3xl border border-border/60 bg-card p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded-2xl" style={{ backgroundColor: `${m.color}1a` }}>
                    <PlatformIcon platform={it.platform} className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="font-heading text-lg font-bold leading-none">{m.label}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{CONNECT_SUB[it.platform]}</p>
                  </div>
                </div>
                <StatusPill status={it.status} />
              </div>

              {it.connected ? (
                <div className="mt-4 space-y-1 rounded-2xl bg-muted/40 p-3 text-sm">
                  <p className="font-semibold">{it.account_name}</p>
                  <p className="text-xs text-muted-foreground">Last synced {relativeTime(it.last_successful_sync)}{it.next_sync ? ` · Next ${relativeTime(it.next_sync).replace(" ago", " from now")}` : ""}</p>
                  {it.sync_warnings && it.sync_warnings.length > 0 && (
                    <div data-testid={`sync-warning-${it.platform}`} className="mt-2 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <div className="space-y-0.5">
                        <p className="font-semibold">Some metrics couldn't be fetched</p>
                        {it.sync_warnings.slice(0, 3).map((w, i) => <p key={i} className="leading-snug">{w}</p>)}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {it.data_available.slice(0, 6).map((d) => <li key={d} className="inline-flex items-center gap-1"><Check className="h-3 w-3" />{d}</li>)}
                </ul>
              )}

              <div className="mt-5 flex flex-1 items-end justify-between">
                <button data-testid={`howto-${it.platform}`} onClick={() => setHowto(it.platform)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">
                  <HelpCircle className="h-3.5 w-3.5" /> How do I connect?
                </button>
                <div className="flex gap-2">
                  {it.connected && it.status === "needs_reconnect" && isAdmin && (
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`reconnect-${it.platform}`} onClick={() => doReconnect(it.platform)} disabled={busy}>Reconnect</Button>
                  )}
                  {it.connected && canSync && (
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`sync-${it.platform}`} onClick={() => doSync(it.platform)} disabled={busy}>
                      <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Sync Now
                    </Button>
                  )}
                  {it.connected ? (
                    <Button size="sm" className="rounded-full bg-brand-primary text-brand-primary-foreground" data-testid={`manage-${it.platform}`} onClick={() => setManage(it)}>
                      <Settings2 className="mr-1.5 h-3.5 w-3.5" /> Manage
                    </Button>
                  ) : isAdmin ? (
                    <Button size="sm" className="nuera-gradient rounded-full text-white" data-testid={`connect-${it.platform}`} onClick={() => startConnect(it.platform)}>
                      Connect <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-full" disabled><Lock className="mr-1.5 h-3.5 w-3.5" />Admin only</Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* sync frequency */}
      <div className="rounded-3xl border border-border/60 bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">Sync frequency</p>
            <p className="text-sm text-muted-foreground">How often Nuera refreshes data from every connected account.</p>
          </div>
          <Select value={String(settings?.frequency_hours || 6)} onValueChange={setFreq} disabled={!canSync}>
            <SelectTrigger className="w-[200px] rounded-full" data-testid="sync-freq"><SelectValue /></SelectTrigger>
            <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={String(f)}>Every {f} hour{f > 1 ? "s" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* ---------- Connect flow dialog ---------- */}
      <Dialog open={!!flow} onOpenChange={(o) => !o && setFlow(null)}>
        <DialogContent className="max-w-md" data-testid="connect-dialog">
          {flow && (() => {
            const p = flow.platform;
            if (flow.step === "prereq") return (
              <>
                <DialogHeader>
                  <span className="mb-2 grid h-11 w-11 place-items-center rounded-xl" style={{ backgroundColor: `${meta.color}1a` }}><PlatformIcon platform={p} className="h-6 w-6" /></span>
                  <DialogTitle>Connect {meta.label}</DialogTitle>
                  <DialogDescription>Before you start, make sure:</DialogDescription>
                </DialogHeader>
                <ul className="space-y-2">
                  {PREREQS[p].map((t) => <li key={t} className="flex items-start gap-2 text-sm"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{t}</li>)}
                </ul>
                <p className="text-xs text-muted-foreground">You'll be redirected to {meta.label} to authorize Nuera. No tokens or technical setup required.</p>
                <DialogFooter>
                  <Button className="nuera-gradient w-full rounded-full text-white" data-testid="connect-continue" onClick={doAuthorize}>Continue with {meta.label}</Button>
                </DialogFooter>
              </>
            );
            if (flow.step === "authorizing" || flow.step === "connecting") return (
              <div className="py-10 text-center">
                <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand-primary" />
                <p className="mt-4 font-medium">{flow.step === "authorizing" ? `Redirecting to ${meta.label}…` : "Connecting your account…"}</p>
                <p className="text-sm text-muted-foreground">Nuera is securely authorizing in the background.</p>
              </div>
            );
            if (flow.step === "select") return (
              <>
                <DialogHeader><DialogTitle>{SELECT_LABEL[p]}</DialogTitle><DialogDescription>Choose the account you'd like Nuera to track.</DialogDescription></DialogHeader>
                <RadioGroup value={flow.selected} onValueChange={(v) => setFlow((f) => ({ ...f, selected: v }))} className="space-y-2">
                  {flow.options.map((o) => (
                    <label key={o.id} htmlFor={o.id} data-testid={`opt-${o.id}`} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${flow.selected === o.id ? "border-brand-primary bg-brand-primary/5" : "border-border/60 hover:bg-muted/40"}`}>
                      <RadioGroupItem value={o.id} id={o.id} />
                      <div><p className="text-sm font-semibold">{o.name}</p><p className="text-xs text-muted-foreground">{o.subtitle}</p></div>
                    </label>
                  ))}
                </RadioGroup>
                <DialogFooter>
                  <Button className="nuera-gradient w-full rounded-full text-white" data-testid="connect-select-confirm" onClick={doConnect}>Connect this account</Button>
                </DialogFooter>
              </>
            );
            if (flow.step === "done") return (
              <div className="py-4 text-center">
                <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50"><Check className="h-7 w-7 text-emerald-600" /></span>
                <h3 className="mt-4 font-heading text-xl font-bold">{meta.label} Connected</h3>
                <p className="text-sm font-medium">{flow.account?.name}</p>
                <ul className="mx-auto mt-4 grid max-w-xs grid-cols-2 gap-1 text-left text-sm">
                  {DATA_LABELS(p).map((d) => <li key={d} className="inline-flex items-center gap-1.5 text-muted-foreground"><Check className="h-3.5 w-3.5 text-emerald-600" />{d}</li>)}
                </ul>
                <Button className="mt-6 w-full rounded-full bg-brand-primary text-brand-primary-foreground" data-testid="connect-done" onClick={() => setFlow(null)}>Done</Button>
              </div>
            );
            if (flow.step === "error") return (
              <div className="py-2 text-center">
                <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-rose-50"><AlertTriangle className="h-7 w-7 text-rose-600" /></span>
                <h3 className="mt-4 font-heading text-lg font-bold">{meta.label} connection failed</h3>
                <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">{ERROR_HINT[p]}</p>
                <div className="mt-6 flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => setHowto(p)}>View instructions</Button>
                  <Button className="nuera-gradient flex-1 rounded-full text-white" onClick={() => setFlow((f) => ({ ...f, step: "prereq" }))}>Try again</Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ---------- Manage dialog ---------- */}
      <Dialog open={!!manage} onOpenChange={(o) => !o && setManage(null)}>
        <DialogContent className="max-w-md" data-testid="manage-dialog">
          {manage && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ backgroundColor: `${PLATFORM_META[manage.platform].color}1a` }}><PlatformIcon platform={manage.platform} className="h-6 w-6" /></span>
                  <DialogTitle>{manage.label}</DialogTitle>
                </div>
                <DialogDescription>Manage this connection — sync data, reconnect or disconnect.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connected account</p><p className="mt-1 font-semibold">{manage.account_name}</p></div>
                <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</p><div className="mt-1"><StatusPill status={manage.status} /></div></div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Data available</p>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-muted-foreground">{manage.data_available.map((d) => <span key={d} className="inline-flex items-center gap-1"><Check className="h-3 w-3" />{d}</span>)}</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Last sync</p><p className="mt-1">{relativeTime(manage.last_successful_sync)}</p></div>
                  <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Next sync</p><p className="mt-1">{manage.next_sync ? relativeTime(manage.next_sync).replace(" ago", " from now") : "—"}</p></div>
                </div>
                <div><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Connection</p><p className="mt-1 text-muted-foreground">Connected via {manage.connected_via}</p></div>
              </div>
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                {canSync && <Button className="w-full rounded-full bg-brand-primary text-brand-primary-foreground" data-testid="manage-sync" onClick={() => doSync(manage.platform)} disabled={busy}><RefreshCw className="mr-1.5 h-4 w-4" />Sync Now</Button>}
                {isAdmin && (
                  <div className="flex w-full gap-2">
                    <Button variant="outline" className="flex-1 rounded-full" data-testid="manage-reconnect" onClick={() => doReconnect(manage.platform)} disabled={busy}>Reconnect</Button>
                    <Button variant="ghost" className="flex-1 rounded-full text-destructive hover:text-destructive" data-testid="manage-disconnect" onClick={() => doDisconnect(manage.platform)} disabled={busy}>Disconnect</Button>
                  </div>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ---------- How-to dialog ---------- */}
      <Dialog open={!!howto} onOpenChange={(o) => !o && setHowto(null)}>
        <DialogContent className="max-w-md" data-testid="howto-dialog">
          {howto && (
            <>
              <DialogHeader><DialogTitle>How to connect {PLATFORM_META[howto].label}</DialogTitle><DialogDescription>A few quick steps — no technical setup needed.</DialogDescription></DialogHeader>
              <ol className="space-y-3">
                {HOWTO[howto].map((step, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-primary/10 text-xs font-bold text-brand-primary">{i + 1}</span>
                    <span className="pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DATA_LABELS(p) {
  return {
    facebook: ["Followers", "Reach", "Impressions", "Engagement", "Content Performance", "Page Views"],
    instagram: ["Followers", "Reach", "Impressions", "Profile Visits", "Engagement", "Content Performance"],
    linkedin: ["Followers", "Impressions", "Clicks", "Reactions", "Shares", "Post Performance"],
    ga4: ["Users", "Sessions", "Engagement", "Page Views", "Traffic Sources", "Landing Pages"],
  }[p] || [];
}
